'use client';

import React, { memo, useMemo } from 'react';

interface AppLogoProps {
  size?: number; // Edge length of the square mark
  className?: string; // Additional classes
  onClick?: () => void; // Click handler
}

/**
 * Brand mark: a solid ink square holding a serif "T". A printer's block —
 * it reads at 24px, needs no colour, and sits in the same visual family as
 * the hairline icon tiles used across the app.
 */
const AppLogo = memo(function AppLogo({ size = 32, className = '', onClick }: AppLogoProps) {
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  return (
    <div className={containerClassName} onClick={onClick}>
      <span
        className="flex-shrink-0 inline-flex items-center justify-center"
        style={{
          width: size,
          height: size,
          background: 'var(--foreground)',
          borderRadius: 3,
          color: 'var(--background)',
          fontFamily: 'var(--font-serif)',
          fontWeight: 600,
          fontSize: Math.round(size * 0.6),
          lineHeight: 1,
          // Optical centering — the serif T sits high in its em box.
          paddingTop: Math.round(size * 0.04),
        }}
        role="img"
        aria-label="TypeSwift logo"
      >
        T
      </span>
    </div>
  );
});

export default AppLogo;
